import React from 'react';
import { createContext, useContext } from 'react';

// Default implementation using window.location
const defaultRouterImplementation = {
  push: (url, state) => {
    if (typeof window !== 'undefined') {
      window.history.pushState(state || {}, '', url);
    }
  },
  replace: (url, state) => {
    if (typeof window !== 'undefined') {
      window.history.replaceState(state || {}, '', url);
    }
  },
  navigate: url => {
    if (typeof window !== 'undefined') {
      window.location.href = url;
    }
  },
  navigateTop: url => {
    if (typeof window !== 'undefined' && window.top) {
      window.top.location.href = url;
    }
  },
  params: {},
  pathname: '',
  search: '',
};

// Create a context for the router
const CheckoutRouterContext = createContext(defaultRouterImplementation);

/**
 * Hook to access the checkout router
 * @returns {Object} Router methods (push, replace, navigate, navigateTop)
 */
export const useCheckoutRouter = () => {
  return useContext(CheckoutRouterContext);
};

/**
 * Provider component for the checkout router
 * @param {Object} props
 * @param {Object} props.router - Custom router implementation
 * @param {Function} props.router.push - Function to navigate to a new URL without full page reload
 * @param {Function} props.router.replace - Function to replace current URL without full page reload
 * @param {Function} props.router.navigate - Function to navigate to a new URL with full page reload
 * @param {Function} props.router.navigateTop - Function to navigate the top window to a new URL
 * @param {Object} props.router.params - Current params
 * @param {string} props.router.pathname - Current pathname
 * @param {string} props.router.search - Current search params
 * @param {React.ReactNode} props.children - Child components
 */
export const CheckoutRouterProvider = ({ router, children }) => {
  const routerImplementation = {
    ...defaultRouterImplementation,
    ...router,
  };

  if (typeof routerImplementation.search === 'string') {
    routerImplementation.search = new URLSearchParams(routerImplementation.search);
  }

  return (
    <CheckoutRouterContext.Provider value={routerImplementation}>
      {children}
    </CheckoutRouterContext.Provider>
  );
};

export default useCheckoutRouter;
